import { applySanMove } from "@chessla/shared";
import { Box, Typography } from "@mui/material";

import {
  ChesslaBoard,
  PLAYED_MOVE_ARROW_COLOR,
  chessArrow,
  isChessSquare,
  type ChessArrow,
} from "@/shared/components/chessboard";

/** Green matches the "Best" marker used in Game Review. */
export const BEST_MOVE_ARROW_COLOR = "#81b64c";

const arrowFor = (fen: string, san: string | null, color: string): ChessArrow[] => {
  const move = san ? applySanMove(fen, san) : null;
  const from = move?.uci.slice(0, 2);
  const to = move?.uci.slice(2, 4);
  return isChessSquare(from) && isChessSquare(to) ? [chessArrow(from, to, color)] : [];
};

/** A static position with the player's move (amber) and the verified best move (green). */
export const PositionThumb = ({
  id,
  fen,
  orientation,
  played,
  best,
}: {
  id: string;
  fen: string;
  orientation: "white" | "black";
  played: string | null;
  best: string | null;
}) => (
  <Box sx={{ width: "100%", maxWidth: 220, flexShrink: 0 }}>
    <ChesslaBoard
      id={id}
      position={fen}
      orientation={orientation}
      allowDragging={false}
      // The played move is drawn last so it stays visible when both arrows share a path.
      arrows={[
        ...(best !== played ? arrowFor(fen, best, BEST_MOVE_ARROW_COLOR) : []),
        ...arrowFor(fen, played, PLAYED_MOVE_ARROW_COLOR),
      ]}
    />
    <Box sx={{ display: "flex", gap: 1.5, mt: 0.5 }}>
      {played && <ArrowKey color={PLAYED_MOVE_ARROW_COLOR} label={`Played ${played}`} />}
      {best && best !== played && <ArrowKey color={BEST_MOVE_ARROW_COLOR} label={`Best ${best}`} />}
    </Box>
  </Box>
);

const ArrowKey = ({ color, label }: { color: string; label: string }) => (
  <Typography sx={{ alignItems: "center", color: "text.secondary", display: "flex", fontSize: 11, gap: 0.5 }}>
    <Box component="span" aria-hidden sx={{ bgcolor: color, display: "inline-block", height: 3, width: 14 }} />
    {label}
  </Typography>
);
